/**
 * Instance config resolver — calls /partners/v1/embedded/resolve with the
 * public token from the URL. Returns the full per-instance config so the
 * SPA can theme itself, gate features, restrict catalog, etc.
 */
import { unauthClient } from '../client';

export interface ResolvedInstanceConfig {
  instance_id: string;
  partner_id: string;
  name: string;
  /**
   * Pinned agent UUID for this instance (nullable). When set, the embed app
   * binds new chat sessions to this agent — `persona.display_name` /
   * `persona.avatar_url` on the same instance can then override the agent's
   * default name/avatar in the chat header without cloning the agents row.
   */
  agent_id: string | null;
  /**
   * Canonical persona JSONB overrides. Authoritative schema lives on
   * PartnerEmbeddedInstance in functions-py partners.py; wg-portal's
   * InstanceShape mirrors it. Split by consumer:
   *   - Agent runtime (livekit-agent injects these into the system prompt
   *     server-side — present in the payload but NOT consumed here):
   *     brand_voice_hints, forbidden_topics, greeting.
   *   - Embed SPA (this client consumes these):
   *     display_name, avatar_url, welcome_messages, seed_content_items,
   *     collapsed.quick_questions.
   * `agent_id` is NOT here — it's the top-level `agent_id` field above.
   * Legacy keys (`tone`, `starter_questions`) are migrated at the data
   * layer and should not appear on new rows. All keys optional.
   */
  persona: {
    display_name?: string;
    avatar_url?: string;
    welcome_messages?: Record<string, string>;
    seed_content_items?: unknown[];
    collapsed?: { quick_questions?: unknown };
    brand_voice_hints?: string;
    forbidden_topics?: string[];
    greeting?: string;
    [key: string]: unknown;
  };
  /**
   * Slim summary of the partner's published partner_listings. The first
   * entry is the flagship (highest display_priority). Used by the embed
   * app to prime UI surfaces (seed cards, header subtitle) and by the
   * agent runtime (server-side) to auto-scope FAQ + listing-details
   * tools to this partner's content. Empty array when the partner has
   * no published listings.
   */
  listings: Array<{
    id: string;
    name: string;
    slug: string;
    booking_external_id: string | null;
    booking_provider: string | null;
  }>;
  theme: Record<string, unknown>;
  catalog_filter: Record<string, unknown>;
  feature_flags: Record<string, unknown>;
  i18n: {
    /**
     * Explicit lock — when set, this locale overrides the visitor's
     * device language. Use rarely (regulatory or partner-specific demos).
     * Added in Phase 6 of embed-pilot-readiness as the production-shaped
     * replacement for the legacy `default_locale` field.
     */
    force_locale?: string;
    /**
     * @deprecated Use `force_locale` instead. Kept readable as a fallback
     * during the transition window; new instances should not set this.
     */
    default_locale?: string;
    supported?: string[];
    /**
     * Pre-set quick-reply prompts shown as chips above the input on the
     * empty-state of Chat. Partner-authored, single language for now —
     * we render verbatim and submit verbatim on tap.
     */
    suggestions?: string[];
  };
  /**
   * Page-context capture configuration. When `enabled` is false (or the
   * field is absent), the bootstrap does not observe parent-page DOM and
   * the iframe never PATCHes browsing context onto the live session.
   */
  page_capture?: {
    enabled?: boolean;
    routes?: { include?: string[]; exclude?: string[] };
    regions?: {
      include_selectors?: string[];
      exclude_selectors?: string[];
    };
    redaction?: { patterns?: string[] };
    actions?: {
      enabled?: boolean;
      denied?: string[];
      auto_grant?: Array<{ verb: string; scope_selector: string }>;
    };
    limits?: {
      max_elements?: number;
      max_regions?: number;
      debounce_ms?: number;
    };
  };
  status: 'draft' | 'published' | 'paused' | 'archived';
  /**
   * Optional campaign code for affiliate attribution on Powered-by footer.
   * Backed by partner_embedded_instances.campaign_code; absent on legacy
   * instances created before the affiliate paired-campaign feature shipped.
   */
  campaign_code?: string;
  /**
   * Provider-agnostic mappings of this instance's "own inventory" to
   * specific external suppliers per catalog provider. The mock checkout
   * picks the default (or first) entry; transformers translate the
   * {provider, external_id} pair to provider-native concepts at the edge.
   */
  supplier_mappings?: Array<{
    provider: string;
    external_id: string;
    label?: string;
    default?: boolean;
  }>;
  /**
   * Per-language UI string bundles. Mirrors the paywall row-per-lang
   * pattern (see migration 20260511024900). Each row is the strings for
   * one BCP-47 lang. Lookup uses pickLocalizedString — full tag → base
   * tag → 'en' → undefined. Legacy single-language fields like
   * theme.launcher.label and i18n.suggestions are still respected when a
   * row for the requested language is absent.
   */
  strings?: Array<{
    lang: string;
    payload: {
      launcher_label?: string;
      welcome_message?: string;
      suggestions?: string[];
      [key: string]: unknown;
    };
  }>;
}

export async function resolveInstance(
  publicToken: string,
  parentOrigin: string | null,
  campaign: string | null = null,
): Promise<ResolvedInstanceConfig> {
  const { data } = await unauthClient.post<ResolvedInstanceConfig>(
    '/partners/v1/embedded/resolve',
    {
      public_token: publicToken,
      parent_origin: parentOrigin,
      // Host-page campaign override; the server returns the EFFECTIVE
      // campaign_code (override only when the instance is blessed).
      ...(campaign ? { campaign } : {}),
    },
  );
  return data;
}
